import { useEffect, useRef } from 'react';

export interface VideoModalProps {
  videoUrl: string;
  onClose: () => void;
  /** Лейбл для aria-label кнопки закрытия. Default «Закрыть видеоинструкцию». */
  closeLabel?: string;
}

/**
 * Модальное окно с YouTube embed-плеером для видео-инструкций (PRD §16.4 / B-008).
 *
 * Использует HTML `<dialog>` element для встроенной поддержки фокус-trap и
 * Escape-key закрытия. iframe указывает на YouTube embed URL (PLACEHOLDER до
 * Phase 8/9 когда продакт даст реальные ссылки).
 */
export function VideoModal({
  videoUrl,
  onClose,
  closeLabel = 'Закрыть видеоинструкцию',
}: VideoModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!dialog.open) {
      // showModal даёт встроенную фокус-trap + Escape. В jsdom это no-op
      // на старых версиях — fallback на `dialog.open = true`.
      try {
        dialog.showModal();
      } catch {
        dialog.setAttribute('open', '');
      }
    }
    const onCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', onCancel);
    return () => {
      dialog.removeEventListener('cancel', onCancel);
    };
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label="Видеоинструкция"
      className="m-auto max-w-[90vw] max-h-[90vh] w-[800px] rounded-xl bg-card p-0 backdrop:bg-black/60"
    >
      <div className="relative bg-card">
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="absolute top-2 right-2 z-10 w-10 h-10 rounded-full bg-card border border-accent text-ink text-xl leading-none focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none"
        >
          ×
        </button>
        <div className="aspect-video w-full">
          <iframe
            src={videoUrl}
            title="Видеоинструкция"
            sandbox="allow-scripts allow-same-origin allow-presentation"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="w-full h-full"
          />
        </div>
      </div>
    </dialog>
  );
}
